import { z } from "zod";

/**
 * Performance over time (docs/health.md): the server's per-minute `server.perf` samples, the
 * clients' session summaries, movement rejects and connection churn, bucketed for the admin
 * Health view (`GET /v1/admin/health/perf`), with the "needs a look" thresholds both sides use.
 */

/** When a signal needs a look. Monitoring thresholds, not game balance. */
export const PERF_THRESHOLDS = {
  /** A minute's tick p95 (ms), half the 50 ms budget. */
  tickP95Ms: 25,
  /** A minute's event-loop delay p99 (ms): timers and sockets served this late. */
  loopP99Ms: 100,
  /** CPU the hypervisor took (%): a shared CPU being throttled. */
  stealPct: 10,
  /** This process's CPU (% of a core). */
  cpuPct: 70,
  /** Resident memory (MB) on a 512 MB machine. */
  rssMb: 400,
  /** Server minutes over a threshold before it flags (one bad minute is noise). */
  minutes: 5,
  /** Wall time no World ticked through, per hour of the window shown (ms) */
  lostMsPerHour: 5000,
  /** A client session's round-trip p95 (ms). */
  rttP95Ms: 250,
  /** A client session's snapshot gap p95 (ms): snapshots are sent every 100 ms. */
  snapGapP95Ms: 250,
  /** Players (summaries with a minute of play or more) over a client threshold before it flags. */
  sessions: 2,
  /** Position corrections per minute of visible play. */
  correctionsPerMinute: 0.5,
  /** Movement rejects (with the throttled ones) an hour, over the window shown */
  moveRejectsPerHour: 30,
  /** Lingers (sockets lost mid-session) an hour, over the window shown */
  lingersPerHour: 6,
} as const;

const N = z.number().nullable();

export const PerfBucketSchema = z.object({
  /** Bucket start (ISO). */
  at: z.string(),
  /** Server minutes sampled in it (0: no server.perf, e.g. before this was deployed). */
  minutes: z.number(),
  players: N,
  /** The worst minute's tick p95 and the longest tick (ms). */
  tickP95: N,
  tickMax: N,
  lostMs: z.number(),
  loopP99: N,
  cpuPct: N,
  stealPct: N,
  rssMb: N,
  snapBytesPerPlayerSec: N,
  /** Client session summaries ending in it: the median session's RTT p50, the worst p95. */
  sessions: z.number(),
  rttP50: N,
  rttP95: N,
  snapGapP95: N,
  corrections: z.number(),
  /** Visible play the summaries cover (minutes): what corrections and long frames are per. */
  clientMinutes: z.number(),
  longFrames: z.number(),
  fpsP10: N,
  moveRejects: z.number(),
  attached: z.number(),
  resumed: z.number(),
  lingered: z.number(),
  reconnects: z.number(),
});
export type PerfBucket = z.infer<typeof PerfBucketSchema>;

export const PerfFlagSchema = z.object({ id: z.string(), text: z.string() });
export type PerfFlag = z.infer<typeof PerfFlagSchema>;

export const PerfSeriesSchema = z.object({
  since: z.string(),
  until: z.string(),
  bucketMinutes: z.number(),
  buckets: z.array(PerfBucketSchema),
  flags: z.array(PerfFlagSchema),
  truncated: z.boolean(),
});
export type PerfSeries = z.infer<typeof PerfSeriesSchema>;
